/**
 * Модалка «План работ» вкладки «Абоненты» (ecology-subscription, макет панели администратора 16.09):
 * работы на год с отметками «выполнено», сроком текстом («до 01.02.2026», «январь») и площадкой
 * (или «вся организация» — решение заказчика по варианту Б). Порядок строк — порядок показа у
 * клиента; сохранение заменяет план выбранного года целиком.
 *
 * План ведётся по годам (ответ заказчика 18.09): прошлые годы остаются историей, а невыполненные
 * работы переносятся на следующий год одной кнопкой — он же становится текущим для клиента.
 */
import { useEffect, useId, useState, type FormEvent } from 'react'
import { Plus, X } from 'lucide-react'
import {
  teamEcologyApi,
  teamEcologySubscriptionsApi,
  type EcoPlanItemInput,
  type EcoSite,
  type EcoSubscription,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

/** Значение-заглушка Radix Select: работа по всей организации (пустое значение пункта недопустимо). */
const WHOLE_ORG = 'org'

function planFrom(sub: EcoSubscription, year: number): EcoPlanItemInput[] {
  return sub.plan
    .filter((p) => p.year === year)
    .map((p) => ({ siteId: p.siteId, title: p.title, dueText: p.dueText, done: p.done }))
}

/** Годы, между которыми переключается инженер: годы с работами плюс текущий год карточки. */
function planYearsOf(sub: EcoSubscription): number[] {
  const years = new Set<number>([sub.planYear, ...sub.planYears])
  return [...years].sort((a, b) => b - a)
}

export function PlanDialog({
  open,
  clientId,
  clientName,
  subscription,
  onClose,
  onSaved,
}: {
  open: boolean
  clientId: string
  clientName: string
  subscription: EcoSubscription
  onClose: () => void
  onSaved: () => void
}) {
  const formId = useId()
  const [year, setYear] = useState(subscription.planYear)
  const [items, setItems] = useState<EcoPlanItemInput[]>(() =>
    planFrom(subscription, subscription.planYear),
  )
  const [sites, setSites] = useState<EcoSite[]>([])
  const [saving, setSaving] = useState(false)
  const [carrying, setCarrying] = useState(false)
  const [dirty, setDirty] = useState(false)
  // Год, на который переключаются, пока не сохранены правки текущего: ждёт подтверждения.
  const [confirmYear, setConfirmYear] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setYear(subscription.planYear)
    setItems(planFrom(subscription, subscription.planYear))
    setDirty(false)
    setConfirmYear(null)
    setError(null)
  }, [open, subscription])

  // Площадки клиента — для привязки работы; недоступны (нет прав, сеть) — план ведётся по организации.
  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    teamEcologyApi(clientId)
      .overview(controller.signal)
      .then((o) => {
        if (!controller.signal.aborted) setSites(o.sites)
      })
      .catch(() => {
        if (!controller.signal.aborted) setSites([])
      })
    return () => controller.abort()
  }, [open, clientId])

  const patch = (index: number, next: Partial<EcoPlanItemInput>) => {
    setDirty(true)
    setItems((list) => list.map((item, i) => (i === index ? { ...item, ...next } : item)))
  }
  const add = () => {
    setDirty(true)
    setItems((list) => [...list, { siteId: '', title: '', dueText: '', done: false }])
  }
  const remove = (index: number) => {
    setDirty(true)
    setItems((list) => list.filter((_, i) => i !== index))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    setError(null)
    setSaving(true)
    try {
      await teamEcologySubscriptionsApi.savePlan(
        clientId,
        year,
        items.filter((i) => i.title.trim() !== ''),
      )
      setDirty(false)
      onSaved()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить план работ'))
    } finally {
      setSaving(false)
    }
  }

  /** Переключение года: несохранённые правки текущего года пропадут, поэтому сначала спрашиваем. */
  function switchYear(next: number) {
    if (next === year) return
    if (dirty) {
      setConfirmYear(next)
      return
    }
    applyYear(next)
  }

  function applyYear(next: number) {
    setYear(next)
    setItems(planFrom(subscription, next))
    setDirty(false)
    setConfirmYear(null)
    setError(null)
  }

  async function carryOver() {
    if (carrying) return
    setError(null)
    setCarrying(true)
    try {
      await teamEcologySubscriptionsApi.carryOverPlan(clientId, year)
      onSaved()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось перенести работы'))
    } finally {
      setCarrying(false)
    }
  }

  const years = planYearsOf(subscription)
  const unfinished = items.filter((i) => i.title.trim() !== '' && !i.done).length
  const title = `План работ ${year} — ${clientName}`
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-3xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Закрыть
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            <p className="mb-3 text-13 text-txt-secondary">
              Отметки «выполнено» и ближайший выезд клиент видит в блоке «Сопровождение» своего
              кабинета. Срок пишется как удобно: «до 01.02.2026», «январь», «февраль–март 2027».
            </p>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <Select value={String(year)} onValueChange={(v) => switchYear(Number(v))}>
                <SelectTrigger aria-label="Год плана работ" className="w-48 text-left">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y === subscription.planYear ? `${y} — текущий` : `${y} — история`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={carrying || unfinished === 0}
                onClick={() => void carryOver()}
                title={
                  unfinished === 0
                    ? 'Переносить нечего — невыполненных работ нет'
                    : `Перенести невыполненные работы в план ${year + 1} года`
                }
              >
                {carrying ? 'Перенос…' : `Перенести невыполненные в ${year + 1}`}
              </Button>
              {year !== subscription.planYear ? (
                <span className="text-12 text-txt-tertiary">
                  Клиент видит план {subscription.planYear} года
                </span>
              ) : null}
            </div>
            {error ? (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            ) : null}

            {items.length === 0 ? (
              <p className="text-13 text-txt-secondary">
                Работ пока нет — добавьте первую работу плана.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {items.map((item, i) => (
                  <li key={i} className="rounded-10 border border-line p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        className="min-w-56 flex-1"
                        value={item.title}
                        aria-label={`Работа ${i + 1}`}
                        placeholder="Например: Инвентаризация мест накопления отходов"
                        onChange={(e) => patch(i, { title: e.target.value })}
                      />
                      <Input
                        className="w-40"
                        value={item.dueText}
                        aria-label={`Срок работы ${i + 1}`}
                        placeholder="до 01.02.2026"
                        onChange={(e) => patch(i, { dueText: e.target.value })}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        title="Убрать работу"
                        aria-label={`Убрать работу ${i + 1}`}
                        onClick={() => remove(i)}
                      >
                        <X className="size-4 stroke-current" aria-hidden />
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <Select
                        value={item.siteId || WHOLE_ORG}
                        onValueChange={(v) => patch(i, { siteId: v === WHOLE_ORG ? '' : v })}
                      >
                        <SelectTrigger
                          aria-label={`Площадка работы ${i + 1}`}
                          className="w-64 text-left"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={WHOLE_ORG}>вся организация</SelectItem>
                          {sites.map((site) => (
                            <SelectItem key={site.id} value={site.id}>
                              {site.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <LabeledCheckbox
                        checked={item.done}
                        onCheckedChange={(done) => patch(i, { done })}
                      >
                        выполнено
                      </LabeledCheckbox>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <Button
              type="button"
              variant="secondary"
              className="mt-3"
              onClick={add}
              leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
            >
              Добавить работу
            </Button>
          </form>
        </Card>
      </DialogContent>
      <ConfirmDialog
        open={confirmYear !== null}
        title="Перейти к другому году?"
        message={`Несохранённые правки плана ${year} года пропадут.`}
        onClose={() => setConfirmYear(null)}
        buttons={[
          {
            label: 'Перейти',
            variant: 'primary',
            onClick: () => applyYear(confirmYear as number),
          },
          { label: 'Отмена', variant: 'secondary', onClick: () => setConfirmYear(null) },
        ]}
      />
    </Dialog>
  )
}
